import { Inject, Injectable } from '@angular/core';
import { HttpClient, HttpContext } from '@angular/common/http';
import { APP_ENVIRONMENT } from '@shared/providers/environment.provider';
import { map } from 'rxjs';
import { CAMEL_CASE_RESPONSE } from '@shared/constants/http';
import { Environment } from '../../types/environment';
import { Specification, SpecificationWare } from '../../types/specification';
import { ControlRecord, ControlRecordMessageType } from '../../types/control';
import { RecalculateFromField } from '../../types/ware';

@Injectable({
  providedIn: 'root',
})
export class SpecificationRepositoryService {
  private readonly API = `${this.env.apiUrl}/specification`;
  private readonly API_WARE = `${this.env.apiUrl}/specificationWare`;
  constructor(
    @Inject(APP_ENVIRONMENT)
    private readonly env: Environment,
    private readonly http: HttpClient,
  ) {}

  create(cargoId: number) {
    return this.http.post<Specification>(`${this.API}/create`, {
      cargoId,
      id: null,
    });
  }

  update(specification: Specification) {
    return this.http.post<Specification>(`${this.API}/save`, specification);
  }

  getAll() {
    return this.http.get<Specification[]>(`${this.API}/getAll`);
  }

  getById(id: number) {
    return this.http.get<Specification>(`${this.API}/getById`, {
      params: {
        id,
      },
    });
  }

  addOrders(id: number, ids: number[]) {
    return this.http.post(`${this.API}/addOrders`, ids, {
      params: {
        id,
      },
    });
  }

  saveWare(specificationWare: SpecificationWare) {
    return this.http.post(`${this.API_WARE}/save`, specificationWare);
  }

  createWaresFromInvoices(specificationId: number, ids: number[]) {
    return this.http.post<SpecificationWare[]>(
      `${this.API_WARE}/addFromInvoices`,
      ids,
      {
        params: {
          specificationId,
        },
      },
    );
  }

  updateWareItem(ware: SpecificationWare) {
    return this.http.post<SpecificationWare>(`${this.API_WARE}/save`, ware);
  }

  deleteWareItems({
    specificationId,
    ids,
  }: {
    specificationId: number;
    ids: number[];
  }) {
    return this.http.post<SpecificationWare[]>(
      `${this.API_WARE}/deleteByIds`,
      ids,
      {
        params: {
          specificationId,
        },
      },
    );
  }

  validate(id: number) {
    return this.http
      .get<ControlRecord[]>(`${this.API}/validate`, {
        context: new HttpContext().set(CAMEL_CASE_RESPONSE, false),
        params: {
          id,
        },
      })
      .pipe(
        map(items => {
          const res: ControlRecord[] = items.map(item => ({
            ...item,
            message: {
              ...item.message,
              type: item.message.type.toLowerCase() as ControlRecordMessageType,
            },
          }));
          return res;
        }),
      );
  }

  recalculateWeight(params: {
    specificationId: number;
    weight: number;
    fromField: RecalculateFromField;
  }) {
    return this.http.post<SpecificationWare[]>(
      `${this.API_WARE}/calculateWeight`,
      null,
      {
        params,
      },
    );
  }

  findWares(params: {
    specificationId: number;
    tnVed: Nullable<string>;
    description: Nullable<string>;
  }) {
    const filtered: Record<string, any> = {};
    Object.entries(params).forEach(([k, v]) => {
      if (v !== null) {
        filtered[k] = v;
      }
    });

    return this.http.get<SpecificationWare[]>(
      `${this.API_WARE}/getBySpecificationIdAndDescriptionOrTnVed`,
      {
        params: filtered,
      },
    );
  }
}
